import { CameraAlt, Edit, Logout, Verified } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  colors,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Slide,
  TextField,
  Typography,
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import { EditPhoneNumber } from '@app/pages/Profile/EditPhoneNumber';
import { useUserProfileController } from '@app/pages/Profile/useUserProfileController';
import { DebugPanel } from '@components';
import formatPhoneNumber from '@utils/formatPhoneNumber';
import { FC } from 'react';

interface UserProfileProps {
  onOptOut: () => void;
  onShowTermsAndConditions: () => void;
}

export const UserProfile: FC<UserProfileProps> = (props) => {
  const {
    view,
    saving,
    avatarSrc,
    displayName,
    email,
    phone,
    fileInputRef,
    fileError,
    saveError,
    editNameOpen,
    editName,
    showEditPhone,
    showTransition,
    logout,
    handleAvatarClick,
    handleFileChange,
    handleEditNameOpen,
    handleEditNameClose,
    handleEditNameChange,
    handleSaveName,
  } = useUserProfileController();

  return (
    <Container>
      <DebugPanel displayItem={view}></DebugPanel>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          pt: 8,
          pb: 2,
        }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <IconButton
              size="small"
              onClick={handleAvatarClick}
              disabled={saving}
              aria-label="Upload profile photo"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                width: 36,
                height: 36,
              }}
            >
              <CameraAlt fontSize="small" />
            </IconButton>
          }
        >
          <Avatar
            sx={{ height: 118, width: 118, fontSize: 56 }}
            src={avatarSrc}
            alt={displayName}
            slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
          />
        </Badge>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={handleFileChange}
          aria-label="Choose profile photo"
        />
        {fileError && (
          <Typography
            color="error"
            variant="caption"
            sx={{
              mt: 1,
            }}
          >
            {fileError}
          </Typography>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mt: 1, width: '100%' }}>
            {saveError}
          </Alert>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: 1,
          }}
        >
          <Typography variant={'h6'}>{displayName}</Typography>
          <IconButton
            size="small"
            onClick={handleEditNameOpen}
            disabled={saving}
            aria-label="Edit name"
            sx={{ ml: 0.5 }}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant={'subtitle1'} color={'textSecondary'}>
          {email}
        </Typography>
      </Box>
      <List>
        <ListItem sx={{ mb: 2, backgroundColor: colors.common.white, borderRadius: '6px' }}>
          <ListItemText>
            <Typography
              component={'span'}
              sx={{
                pr: 2,
                fontWeight: 400,
                fontSize: 15,
              }}
            >
              Mobile:
            </Typography>
            <Typography
              component={'span'}
              sx={{
                fontWeight: 400,
                fontSize: 15,
              }}
            >
              +1 {formatPhoneNumber(phone)}
            </Typography>
          </ListItemText>
          <NavLink to={'?showModal=edit'}>
            <Edit color={'primary'} />
          </NavLink>
        </ListItem>
        <ListItemButton
          onClick={logout}
          sx={{ backgroundColor: colors.common.white, color: '#EB3223', borderRadius: '6px' }}
        >
          <Logout />
          <ListItemText>
            <Typography
              sx={{
                pl: 2,
                color: '#EB3223',
              }}
            >
              Logout of App
            </Typography>
          </ListItemText>
        </ListItemButton>
      </List>
      <Box
        sx={{
          mt: 2,
        }}
      >
        <Slide direction="right" in={showTransition} mountOnEnter unmountOnExit appear={true}>
          <Alert severity={'success'} icon={<Verified />} sx={{ fontFamily: 'Roboto', fontSize: 13 }}>
            Your new mobile number has been updated
          </Alert>
        </Slide>
      </Box>
      <Box sx={{ position: 'absolute', bottom: 8 }}>
        <Box
          sx={{
            backgroundColor: 'rgba(111,167,171,0.16)',
            borderRadius: '6px',
            p: 2,
            ml: -1,
            mr: 1,
          }}
        >
          <Typography
            component={'p'}
            sx={{
              fontWeight: 400,
              fontSize: 11,
            }}
          >
            By opting into SMS text messages from We Hate Exercise, you are agreeing to receive recurring messages.
            Message frequency may vary. Message and data rates may apply. You can opt out at any time by replying STOP
            or HELP for any support
          </Typography>
          <Typography
            component={'p'}
            sx={{
              mt: 2,
              fontWeight: 400,
              fontSize: 11,
            }}
          >
            Click Here to{' '}
            <Link href={'#'} onClick={props.onOptOut} sx={{ color: colors.common.black }}>
              Opt Out of Message
            </Link>
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: 2,
          }}
        >
          <Link
            href={'#'}
            onClick={props.onShowTermsAndConditions}
            sx={{
              fontSize: 10,
            }}
          >
            WHE Terms & Conditions and Privacy Policy
          </Link>
        </Box>
      </Box>
      <EditPhoneNumber open={showEditPhone} phoneNumber={phone}></EditPhoneNumber>
      <Dialog open={editNameOpen} onClose={handleEditNameClose} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={editName}
            onChange={(e) => handleEditNameChange(e.target.value)}
            margin="dense"
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditNameClose}>Cancel</Button>
          <Button onClick={handleSaveName} disabled={saving || !editName.trim()} variant="contained">
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
